import { FC, useContext } from 'react';
import classNames from 'classnames';
import dayjs from 'dayjs';
import { Users, CalendarDays, Check, CircleHelp } from 'lucide-react';
import GenericModal from '../../../shared/GenericModal/GenericModal';
import CustomButton from '../../../shared/CustomButton/CustomButton';
import CustomTextInput from '../../../shared/CustomInput/CustomTextInput';
import CustomSelectInput from '../../../shared/CustomSelectInput/CustomSelectInput';
import Typhography from '../../../shared/Typhography/Typhography';
import { CustomBadge } from '../../../shared/CustomBadge/CustomBadge';
import { InfoDrawerContext } from '../../../providers/InfoDrawer';
import { buildInviteUrl } from '../../../endpoints/invites';
import {
  EXPIRY_OPTIONS,
  MAX_USES_OPTIONS,
  useTeamInvites,
} from './useTeamInvites';
import styles from './TeamInvitesModal.module.scss';

interface ITeamInvitesModal {
  teamId: string;
  isOpen: boolean;
  onClose: () => void;
}

/** Etichetta leggibile del motivo per cui un link non è più utilizzabile. */
const INVALID_STATE_LABELS: Record<string, string> = {
  revoked: 'Disattivato',
  expired: 'Scaduto',
  full: 'Posti esauriti',
  not_found: 'Non valido',
};

/**
 * Creazione e gestione dei link di invito, in una modal.
 *
 * Prima era un pannello che si apriva *dentro* la pagina Atleti, spingendo la
 * tabella del roster mezzo schermo più in basso: un'azione occasionale che
 * rubava spazio permanente al contenuto principale. Qui l'operazione ha la sua
 * superficie, e la pagina sotto resta quella che il coach guarda ogni giorno.
 */
export const TeamInvitesModal: FC<ITeamInvitesModal> = ({
  teamId,
  isOpen,
  onClose,
}) => {
  const { setState: openInfoDrawer } = useContext(InfoDrawerContext);
  const {
    invites,
    isLoading,
    error,
    isCreating,
    copiedCode,
    maxUses,
    expiresInDays,
    label,
    setMaxUses,
    setExpiresInDays,
    setLabel,
    createInvite,
    toggleRevoke,
    copyLink,
  } = useTeamInvites(teamId, isOpen);

  return (
    <GenericModal
      isOpen={isOpen}
      onClose={onClose}
      size="xl"
      title="Invita atleti con un link"
      Footer={
        <div className={styles.footer}>
          <CustomButton color="outline" size="sm" onClick={onClose}>
            Chiudi
          </CustomButton>
        </div>
      }
    >
      <div className={styles.body}>
        <div className={styles.intro}>
          <Typhography size="regular-14" className={styles.description}>
            Genera un link e mandalo agli atleti: chi lo apre entra da solo in
            questa squadra. Se ha già un account LeoPart gli basta un clic,
            altrimenti si registra al momento.
          </Typhography>
          <button
            type="button"
            className={styles.help}
            onClick={() => openInfoDrawer('teamInvites')}
            title="Come funzionano i link di invito"
            aria-label="Come funzionano i link di invito"
          >
            <CircleHelp size={16} strokeWidth={2.2} />
            Come funziona
          </button>
        </div>

        {/* ── Creazione ──────────────────────────────────────────────────── */}
        <div className={styles.form}>
          <div className={styles.fieldWide}>
            <CustomTextInput
              label="Nome (facoltativo)"
              placeholder="Es. Ritiro estivo"
              value={label}
              onChange={(event) => setLabel(event.target.value)}
            />
          </div>

          <div className={styles.field}>
            {/* CustomSelectInput lavora sul valore grezzo, non sull'option:
                `value` è il numero e `onChange` riceve già `option.value`. */}
            <CustomSelectInput
              label="Posti"
              options={MAX_USES_OPTIONS}
              value={maxUses}
              onChange={(value) => setMaxUses(Number(value))}
            />
          </div>

          <div className={styles.field}>
            <CustomSelectInput
              label="Valido per"
              options={EXPIRY_OPTIONS}
              value={expiresInDays}
              onChange={(value) => setExpiresInDays(Number(value))}
            />
          </div>

          <CustomButton
            color="secondary"
            size="sm"
            className="team-invites-create"
            isLoading={isCreating}
            disabled={isCreating}
            onClick={createInvite}
          >
            Crea link
          </CustomButton>
        </div>

        {error && <div className={styles.error}>{error}</div>}

        {/* ── Link esistenti ─────────────────────────────────────────────── */}
        {isLoading && (
          <Typhography size="regular-14" className={styles.empty}>
            Carico i link…
          </Typhography>
        )}

        {!isLoading && !invites.length && (
          <Typhography size="regular-14" className={styles.empty}>
            Nessun link ancora creato per questa squadra.
          </Typhography>
        )}

        {!isLoading && invites.length > 0 && (
          <div className={styles.list}>
            <Typhography size="bold-12" className={styles.listTitle}>
              Link creati
            </Typhography>

            {invites.map((invite) => {
              const isActive = invite.state === null;
              const expiryLabel = invite.expiresAt
                ? dayjs(invite.expiresAt).format('DD/MM/YYYY')
                : 'nessuna scadenza';
              const usageLabel =
                invite.maxUses == null
                  ? `${invite.usedCount} usati`
                  : `${invite.usedCount}/${invite.maxUses} usati`;

              return (
                <div
                  key={invite._id}
                  className={classNames(styles.row, {
                    [styles.rowDisabled]: !isActive,
                  })}
                >
                  <div className={styles.info}>
                    <Typhography size="regular-14" className={styles.url}>
                      {invite.label && (
                        <span className={styles.label}>{invite.label} · </span>
                      )}
                      {buildInviteUrl(invite.code)}
                    </Typhography>
                    <div className={styles.meta}>
                      <span className={styles.metaItem}>
                        <Users size={12} strokeWidth={2.2} /> {usageLabel}
                      </span>
                      <span className={styles.metaItem}>
                        <CalendarDays size={12} strokeWidth={2.2} /> {expiryLabel}
                      </span>
                      <span className={styles.metaItem}>
                        <Check size={12} strokeWidth={2.6} /> {invite.redemptions}{' '}
                        entrati
                      </span>
                    </div>
                  </div>

                  <CustomBadge
                    color={isActive ? 'lightgreen' : 'lightred'}
                    label={
                      isActive
                        ? 'Attivo'
                        : INVALID_STATE_LABELS[invite.state ?? 'not_found']
                    }
                  />

                  <div className={styles.actions}>
                    <CustomButton
                      color="outline"
                      size="xs"
                      onClick={() => copyLink(invite.code)}
                    >
                      {copiedCode === invite.code ? 'Copiato!' : 'Copia'}
                    </CustomButton>
                    <CustomButton
                      color="outline"
                      size="xs"
                      onClick={() => toggleRevoke(invite)}
                    >
                      {invite.revokedAt ? 'Riattiva' : 'Disattiva'}
                    </CustomButton>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </GenericModal>
  );
};

export default TeamInvitesModal;
